import React from 'react';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {light} from '@fortawesome/fontawesome-svg-core/import.macro';
import {t} from '../../../../../../services/i18n';
import {Colors} from '../../../../../../styles/Colors';
import {GlobalStyleAttributes} from '../../../../../../styles/GlobalStyles';
import HStack from '../../../../../common/Layout/HStack';
import VStack from '../../../../../common/Layout/VStack';
import AppText from '../../../../../common/AppText';
import { useSelector } from 'react-redux';
import { useMemo } from 'react';
import { formatPriceWithCurrencyLTR, sortCourseEventDays, dbTimeFormat } from '../../../../../../services/Helpers';
import moment from 'moment';
import { solid } from '@fortawesome/fontawesome-svg-core/import.macro';
import { GlobalConsts } from '../../../../../../styles/GlobalConsts';
import { MembershipTypes } from '../../../../../../Configs/DatabaseConsts';
import { getPartialAttendanceDisplay } from '../../../../../../services/Helpers';

const HugCollapseBody = ({ hug }) => {
    const box = useSelector((state) => state.box.box);
    const lang = useSelector((state) => state.userSettings.lang);
    const currencySymbol = useMemo(() => box?.locations_box?.[0]?.currency_symbol ?? '₪', [box]);
    const boxLocale = useMemo(() => lang?.language === 'Hebrew' ? 'he' : 'en', [lang]);
    const timeFormat = useMemo(() => box?.locations_box?.[0]?.time_format, [box]);
    let days = useMemo(() => hug ? sortCourseEventDays(hug.series_events) : [], [hug])

    const recurringMT = useMemo(() => hug.membership_types_all?.find(mt => mt.type === MembershipTypes.RECURRING && !mt.membership_types_partial_props), [hug]);
    const regularMT = useMemo(() => hug.membership_types_all?.find(mt => mt.type === MembershipTypes.REGULAR && !mt.membership_types_partial_props), [hug]);
    const recurringPartialOptions = useMemo(() => (hug.partialBookingOptions?.filter(o => o.type === MembershipTypes.RECURRING) ?? []), [hug]);
    const regularPartialOptions = useMemo(() => (hug.partialBookingOptions?.filter(o => o.type === MembershipTypes.REGULAR) ?? []), [hug]);

    const formatPrice = (amount) => formatPriceWithCurrencyLTR(amount, currencySymbol);
    const customFieldsData = hug.custom_field_value?.length > 0 ? hug.custom_field_value : hug.series?.custom_field_value;
    const customFieldsDataFiltered = customFieldsData?.filter(field => field.value);
    const ageRestrictionDetail = hug.min_age != null
        ? (hug.max_age != null
            ? t('course-age-between-summary', {minAge: hug.min_age, maxAge: hug.max_age})
            : t('course-age-from-summary', {minAge: hug.min_age}))
        : null;

    return (
        <HStack space={'2rem'} alignItems={'flex-start'} flexWrap={'wrap'}>
            <VStack space={'0.25rem'} style={{ minWidth: '120px' }}>
                <AppText fontSize={GlobalStyleAttributes.fonts.heading3} boldFont>
                    {t('days-and-hours')}
                </AppText>
                {days.map((day, i) =>
                <HStack key={`day-${i}`} space={'0.5rem'} flex={'unset'} alignItems={'center'}>
                    {`${moment().weekday(day.day_of_week).locale(boxLocale).format('dddd')}: ${moment(day.start, dbTimeFormat).locale(boxLocale).format(timeFormat)} - ${moment(day.end, dbTimeFormat).locale(boxLocale).format(timeFormat)}${day.description ? ` - ${day.description}` : ''}`}
                    <FontAwesomeIcon icon={solid('circle-small')} fontSize={'5px'}/>

                    <HStack space={'0.5rem'} flex={'unset'} alignItems={'center'}>
                        <FontAwesomeIcon icon={solid('user')} fontSize={GlobalConsts.iconSize.XXS}/>
                        <AppText>{getPartialAttendanceDisplay(day.id, hug)}</AppText>
                    </HStack>
                </HStack>)}
                {hug.has_started && (
                <AppText fontSize={GlobalStyleAttributes.fonts.heading3}>
                    {`*${t('course-already-started')}`}
                </AppText>
        )}
            </VStack>
        {hug.coach && (
            <VStack space={'0.25rem'} style={{ minWidth: '120px' }}>
                <AppText fontSize={GlobalStyleAttributes.fonts.heading3} boldFont>
                    {hug.second_coach ? t('staff-members') : t('staff-member')}
                </AppText>
                <AppText fontSize={GlobalStyleAttributes.fonts.heading3}>
                    {`${hug.coach.first_name} ${hug.coach.last_name || ''}`}
                </AppText>
                {hug.second_coach && <AppText fontSize={GlobalStyleAttributes.fonts.heading3}>
                    {`${hug.second_coach.first_name} ${hug.second_coach.last_name || ''}`}
                </AppText>}

            </VStack>
        )}
        <VStack space={'0.35rem'} style={{ minWidth: '120px' }}>
        {(recurringMT || recurringPartialOptions.length > 0) && (
            <VStack space={'0.2rem'} style={{ minWidth: '120px' }}>
                <AppText fontSize={GlobalStyleAttributes.fonts.heading3} boldFont>
                    {t('prices-in-hok')}
                </AppText>
                {recurringMT && (
                    <AppText fontSize={GlobalStyleAttributes.fonts.heading3}>
                        {t('full-course-price-hok', { price: formatPrice(recurringMT.price) })}
                    </AppText>
                )}
                {recurringPartialOptions.map(option => (
                    <AppText key={option.id} fontSize={GlobalStyleAttributes.fonts.heading3}>
                        {t('partial-course-price', { occurances: t(`${option.numOfDays}-days-occurances`), price: formatPrice(option.price), interpolation: { escapeValue: false } })}
                    </AppText>
                ))}
            </VStack>
        )}
        {(regularMT || regularPartialOptions.length > 0) && (
            <VStack space={'0.2rem'} style={{ minWidth: '120px' }}>
                <AppText fontSize={GlobalStyleAttributes.fonts.heading3} boldFont>
                    {t('prices-in-regular')}
                </AppText>
                {regularMT && (
                    <AppText fontSize={GlobalStyleAttributes.fonts.heading3}>
                        {t('full-course-price', { price: formatPrice(regularMT.price) })}
                    </AppText>
                )}
                {regularPartialOptions.map(option => (
                    <AppText key={option.id} fontSize={GlobalStyleAttributes.fonts.heading3}>
                        {t('partial-course-price', { occurances: t(`${option.numOfDays}-days-occurances`), price: formatPrice(option.price), interpolation: { escapeValue: false } })}
                    </AppText>
                ))}
                {regularMT?.max_installments > 1 && (
                    <AppText fontSize={GlobalStyleAttributes.fonts.heading3}>
                        {t('up-to-installments', { max_installments: regularMT.max_installments })}
                    </AppText>
                )}
            </VStack>
        )}
        </VStack>

        <VStack space={'0.25rem'} style={{ minWidth: '120px' }}>
        {customFieldsDataFiltered?.map((field,index) => {
                const value = field.custom_field.props.options.find(option => option.value === field.value)?.label;
                return <HStack key={field.id} space={'0.5rem'} flex={'unset'} alignItems={'center'}>
                    <AppText fontSize={GlobalStyleAttributes.fonts.heading3} boldFont>{field.custom_field.name}:</AppText>
                    <AppText fontSize={GlobalStyleAttributes.fonts.heading3}>{value}</AppText>
                </HStack>
            })}
            {ageRestrictionDetail && (
                <VStack space={'0.2rem'}>
                    <AppText fontSize={GlobalStyleAttributes.fonts.heading3} boldFont>{t('course-age-restriction-label')}:</AppText>
                    <AppText fontSize={GlobalStyleAttributes.fonts.heading3}>{ageRestrictionDetail}</AppText>
                </VStack>
            )}
        </VStack>
    </HStack>
    )
};

export default HugCollapseBody;
